import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';
import type {
  AnalyticsChart,
  AnalyticsChartData,
  AnalyticsDashboard,
  AnalyticsDashboardData,
  AnalyticsDataset,
  AnalyticsDatasetField,
  AnalyticsEmbedTokenRequest,
  AnalyticsEmbedTokenResponse,
  AnalyticsFilterContainer,
  AnalyticsSourceColumn,
  AnalyticsSourceTable,
  AnalyticsSourceView,
  AnalyticsStatus,
  AnalyticsDatasetSourceType,
} from '../../pages/AnalyticalTool/types';

type AnalyticsListParams = {
  status?: AnalyticsStatus;
  sourceType?: AnalyticsDatasetSourceType;
  datasetId?: number;
};

type ChartDataRequest = {
  filters?: AnalyticsFilterContainer;
  globalFilters?: AnalyticsFilterContainer;
  limit?: number;
};

type DatasetPayload = Partial<AnalyticsDataset> &
  Pick<AnalyticsDataset, 'name' | 'sourceType'>;

type ChartPayload = Omit<
  Partial<AnalyticsChart>,
  'dataset' | 'createdByUser' | 'updatedByUser'
> &
  Pick<AnalyticsChart, 'datasetId' | 'name' | 'queryConfig'>;

type DashboardPayload = Omit<
  Partial<AnalyticsDashboard>,
  'createdByUser' | 'updatedByUser'
>;

const analyticalToolApi = createApi({
  reducerPath: 'analyticalTool',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['AnalyticsDataset', 'AnalyticsChart', 'AnalyticsDashboard'],
  endpoints: builder => ({
    getAnalyticsTables: builder.query<BackendResponse<AnalyticsSourceTable[]>, void>({
      query: () => ({ url: '/analytical-tool/sources/tables', method: 'GET' }),
    }),
    getAnalyticsViews: builder.query<BackendResponse<AnalyticsSourceView[]>, void>({
      query: () => ({ url: '/analytical-tool/sources/views', method: 'GET' }),
    }),
    getAnalyticsColumns: builder.query<
      BackendResponse<AnalyticsSourceColumn[]>,
      string
    >({
      query: sourceName => ({
        url: `/analytical-tool/sources/${sourceName}/columns`,
        method: 'GET',
      }),
    }),
    createAnalyticsDataset: builder.mutation<
      BackendResponse<AnalyticsDataset>,
      DatasetPayload
    >({
      query: body => ({
        url: '/analytical-tool/datasets',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AnalyticsDataset'],
    }),
    getAnalyticsDatasets: builder.query<
      BackendResponse<AnalyticsDataset[]>,
      AnalyticsListParams | void
    >({
      query: params => ({
        url: `/analytical-tool/datasets${parseQueryParams(params || {})}`,
        method: 'GET',
      }),
      providesTags: ['AnalyticsDataset'],
    }),
    getAnalyticsDataset: builder.query<BackendResponse<AnalyticsDataset>, number>({
      query: id => ({ url: `/analytical-tool/datasets/${id}`, method: 'GET' }),
      providesTags: (_, __, id) => [{ type: 'AnalyticsDataset', id }],
    }),
    updateAnalyticsDataset: builder.mutation<
      BackendResponse<AnalyticsDataset>,
      { id: number; data: Partial<DatasetPayload> }
    >({
      query: ({ id, data }) => ({
        url: `/analytical-tool/datasets/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [
        'AnalyticsDataset',
        { type: 'AnalyticsDataset', id },
      ],
    }),
    deleteAnalyticsDataset: builder.mutation<BackendResponse<AnalyticsDataset>, number>({
      query: id => ({ url: `/analytical-tool/datasets/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AnalyticsDataset'],
    }),
    introspectAnalyticsDataset: builder.mutation<
      BackendResponse<AnalyticsDatasetField[]>,
      number
    >({
      query: id => ({
        url: `/analytical-tool/datasets/${id}/introspect`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, id) => [{ type: 'AnalyticsDataset', id }],
    }),
    previewAnalyticsDataset: builder.mutation<
      BackendResponse<AnalyticsChartData>,
      { id: number; limit?: number }
    >({
      query: ({ id, limit }) => ({
        url: `/analytical-tool/datasets/${id}/preview`,
        method: 'POST',
        body: { limit },
      }),
    }),
    updateAnalyticsDatasetFields: builder.mutation<
      BackendResponse<AnalyticsDatasetField[]>,
      { id: number; fields: AnalyticsDatasetField[] }
    >({
      query: ({ id, fields }) => ({
        url: `/analytical-tool/datasets/${id}/fields`,
        method: 'PATCH',
        body: { fields },
      }),
      invalidatesTags: (_, __, { id }) => [
        'AnalyticsDataset',
        { type: 'AnalyticsDataset', id },
      ],
    }),
    createAnalyticsChart: builder.mutation<
      BackendResponse<AnalyticsChart>,
      ChartPayload
    >({
      query: body => ({
        url: '/analytical-tool/charts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AnalyticsChart'],
    }),
    getAnalyticsCharts: builder.query<
      BackendResponse<AnalyticsChart[]>,
      AnalyticsListParams | void
    >({
      query: params => ({
        url: `/analytical-tool/charts${parseQueryParams(params || {})}`,
        method: 'GET',
      }),
      providesTags: ['AnalyticsChart'],
    }),
    getAnalyticsChart: builder.query<BackendResponse<AnalyticsChart>, number>({
      query: id => ({ url: `/analytical-tool/charts/${id}`, method: 'GET' }),
      providesTags: (_, __, id) => [{ type: 'AnalyticsChart', id }],
    }),
    updateAnalyticsChart: builder.mutation<
      BackendResponse<AnalyticsChart>,
      { id: number; data: Partial<ChartPayload> }
    >({
      query: ({ id, data }) => ({
        url: `/analytical-tool/charts/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [
        'AnalyticsChart',
        { type: 'AnalyticsChart', id },
      ],
    }),
    deleteAnalyticsChart: builder.mutation<BackendResponse<AnalyticsChart>, number>({
      query: id => ({ url: `/analytical-tool/charts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AnalyticsChart', 'AnalyticsDashboard'],
    }),
    getAnalyticsChartData: builder.mutation<
      BackendResponse<AnalyticsChartData>,
      { id: number; body?: ChartDataRequest }
    >({
      query: ({ id, body }) => ({
        url: `/analytical-tool/charts/${id}/data`,
        method: 'POST',
        body: body || {},
      }),
    }),
    getAnalyticsChartSql: builder.query<
      BackendResponse<{ chartId: number; datasetId: number; datasetSql: string; chartSql?: string | null }>,
      number
    >({
      query: id => ({ url: `/analytical-tool/charts/${id}/sql`, method: 'GET' }),
    }),
    createAnalyticsDashboard: builder.mutation<
      BackendResponse<AnalyticsDashboard>,
      DashboardPayload
    >({
      query: body => ({
        url: '/analytical-tool/dashboards',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AnalyticsDashboard'],
    }),
    getAnalyticsDashboards: builder.query<
      BackendResponse<AnalyticsDashboard[]>,
      AnalyticsListParams | void
    >({
      query: params => ({
        url: `/analytical-tool/dashboards${parseQueryParams(params || {})}`,
        method: 'GET',
      }),
      providesTags: ['AnalyticsDashboard'],
    }),
    getAnalyticsDashboard: builder.query<BackendResponse<AnalyticsDashboard>, number>({
      query: id => ({ url: `/analytical-tool/dashboards/${id}`, method: 'GET' }),
      providesTags: (_, __, id) => [{ type: 'AnalyticsDashboard', id }],
    }),
    updateAnalyticsDashboard: builder.mutation<
      BackendResponse<AnalyticsDashboard>,
      { id: number; data: DashboardPayload }
    >({
      query: ({ id, data }) => ({
        url: `/analytical-tool/dashboards/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [
        'AnalyticsDashboard',
        { type: 'AnalyticsDashboard', id },
      ],
    }),
    deleteAnalyticsDashboard: builder.mutation<
      BackendResponse<AnalyticsDashboard>,
      number
    >({
      query: id => ({
        url: `/analytical-tool/dashboards/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AnalyticsDashboard'],
    }),
    getAnalyticsDashboardData: builder.mutation<
      BackendResponse<AnalyticsDashboardData>,
      { id: number; body?: ChartDataRequest }
    >({
      query: ({ id, body }) => ({
        url: `/analytical-tool/dashboards/${id}/data`,
        method: 'POST',
        body: body || {},
      }),
    }),
    createAnalyticsEmbedToken: builder.mutation<
      BackendResponse<AnalyticsEmbedTokenResponse>,
      { dashboardId: number; data: AnalyticsEmbedTokenRequest }
    >({
      query: ({ dashboardId, data }) => ({
        url: `/analytical-tool/dashboards/${dashboardId}/embed-token`,
        method: 'POST',
        body: data,
      }),
    }),
    getEmbeddedAnalyticsDashboard: builder.query<
      BackendResponse<{ dashboard: AnalyticsDashboard; embed: Record<string, unknown> }>,
      { dashboardId: number; token: string }
    >({
      query: ({ dashboardId, token }) => ({
        url: `/analytical-tool/embed/dashboards/${dashboardId}?token=${encodeURIComponent(token)}`,
        method: 'GET',
      }),
    }),
    getEmbeddedAnalyticsChartData: builder.mutation<
      BackendResponse<AnalyticsChartData>,
      { dashboardId: number; chartId: number; token: string; body?: ChartDataRequest }
    >({
      query: ({ dashboardId, chartId, token, body }) => ({
        url: `/analytical-tool/embed/dashboards/${dashboardId}/charts/${chartId}/data?token=${encodeURIComponent(token)}`,
        method: 'POST',
        body: body || {},
      }),
    }),
  }),
});

export default analyticalToolApi;

export const {
  useGetAnalyticsTablesQuery,
  useGetAnalyticsViewsQuery,
  useGetAnalyticsColumnsQuery,
  useCreateAnalyticsDatasetMutation,
  useGetAnalyticsDatasetsQuery,
  useGetAnalyticsDatasetQuery,
  useUpdateAnalyticsDatasetMutation,
  useDeleteAnalyticsDatasetMutation,
  useIntrospectAnalyticsDatasetMutation,
  usePreviewAnalyticsDatasetMutation,
  useUpdateAnalyticsDatasetFieldsMutation,
  useCreateAnalyticsChartMutation,
  useGetAnalyticsChartsQuery,
  useGetAnalyticsChartQuery,
  useUpdateAnalyticsChartMutation,
  useDeleteAnalyticsChartMutation,
  useGetAnalyticsChartDataMutation,
  useGetAnalyticsChartSqlQuery,
  useLazyGetAnalyticsChartSqlQuery,
  useCreateAnalyticsDashboardMutation,
  useGetAnalyticsDashboardsQuery,
  useGetAnalyticsDashboardQuery,
  useUpdateAnalyticsDashboardMutation,
  useDeleteAnalyticsDashboardMutation,
  useGetAnalyticsDashboardDataMutation,
  useCreateAnalyticsEmbedTokenMutation,
  useGetEmbeddedAnalyticsDashboardQuery,
  useGetEmbeddedAnalyticsChartDataMutation,
} = analyticalToolApi;
